import { Component, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AutenticacaoService } from "@cei-ufg/ngx-cei-sso-client";
import { CeiSnackBarService } from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { NgxTurnstileFormsModule, NgxTurnstileModule } from 'ngx-turnstile';
import { CaptchaService } from '../../services/captcha-service';
import { runtimeConfig } from '../../app.config';

@Component({
  selector: 'app-autenticacao',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    RouterModule,
    NgxTurnstileModule,
    NgxTurnstileFormsModule,
],
  templateUrl: './autenticacao-component.html',
  styleUrl: './autenticacao-component.scss',
  standalone: true
})
export class AutenticacaoComponent {
  loginForm!: FormGroup;
  erroLogin: string | null = null;
  loading = false;

  private redirectUrl: string = '/sucesso-autenticacao';
  /** @deprecated legado — apenas leitura de query antiga */
  private legacyClientAppUrl?: string;
  /** @deprecated legado */
  private legacyClientApiUrl?: string;
  public captchaKey !: string;

  private readonly snackBar = inject(CeiSnackBarService);

  constructor(
    private fb: FormBuilder,
    private autenticacaoService: AutenticacaoService,
    private router: Router,
    private route: ActivatedRoute,
    private captchaService: CaptchaService
  ) { }

  ngOnInit(): void {
    this.captchaKey = runtimeConfig?.captchaKey || environment.captchaKey;
    this.loginForm = this.fb.group({
      usuario: ['', [Validators.required]],
      senha: ['', [Validators.required]],
      captchaToken: new FormControl(null, Validators.required)
    });

    this.route.queryParams.subscribe(params => {
        if (params['redirect_url']) {
            this.redirectUrl = params['redirect_url'];
        }

        if (params['logout'] === '1') {
            const redirectBack =
              typeof params['redirect_url'] === 'string' ? params['redirect_url'] : undefined;
            this.autenticacaoService
              .logoutRemoteSessionOnly()
              .pipe(
                finalize(() => {
                  if (redirectBack) {
                    window.location.replace(this.resolveReturnUrlAfterLogout(redirectBack));
                    return;
                  }
                  this.router.navigate([], {
                    queryParams: { logout: null },
                    queryParamsHandling: 'merge',
                    replaceUrl: true,
                  });
                }),
              )
              .subscribe();
        }

        if (params['client_app_url']) {
            this.legacyClientAppUrl = params['client_app_url'];
            console.warn('client_app_url na query está obsoleto; use redirect_url + urlBaseApiCliente no deploy do SSO.');
        }

        if (params['client_api_url']) {
            this.legacyClientApiUrl = params['client_api_url'];
            console.warn('client_api_url na query está obsoleto; configure urlBaseApiCliente no deploy do SSO.');
        }
    });
  }

  autenticar(): void {
    this.erroLogin = null;

    if (this.loginForm?.invalid) {
      this.loginForm.markAllAsTouched();
      this.erroLogin = 'Por favor, preencha o formulário corretamente.';
      return;
    }

    this.loading = true;

    const loginPayload = {
      ...this.loginForm.value,
      redirect_url: this.resolveRedirectUrlForBackend(),
    };

    this.autenticacaoService.login(loginPayload)
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (res) => {
          if (res?.callback_url) {
            window.location.href = res.callback_url;
            return;
          }

          const accessToken =
            res?.access_token ??
            res?.access ??
            res?.token ??
            res?.accessToken ??
            null;
          this.executarRedirecionamentoLegado(accessToken);
        },
        error: (error) => {
          console.error('Erro no login:', error);
          const redirectError = error?.error?.redirect_url;
          this.erroLogin = redirectError
            ? String(redirectError)
            : 'Credenciais inválidas. Verifique usuário e senha.';
          this.snackBar.error('Houve um erro ao realizar o login');
          this.loginForm.reset();
        }
      });
  }

  private resolveUrlBaseApiCliente(): string {
    const legacy = runtimeConfig as { authApiUrl?: string; urlApi?: string } | undefined;
    return (
      runtimeConfig?.urlBaseApiCliente ||
      legacy?.urlApi ||
      legacy?.authApiUrl ||
      environment.urlBaseApiCliente ||
      (environment as { authApiUrl?: string }).authApiUrl ||
      (environment as { urlApi?: string }).urlApi ||
      runtimeConfig?.sso?.urlBaseServicoSSO ||
      environment.sso.urlBaseServicoSSO
    ).replace(/\/+$/, '');
  }

  /** Volta ao app cliente após handshake ?logout=1 no SSO remoto. */
  private resolveReturnUrlAfterLogout(redirectBack: string): string {
    let candidate = redirectBack.trim();
    for (let i = 0; i < 3; i += 1) {
      try {
        const decoded = decodeURIComponent(candidate);
        if (decoded === candidate) {
          break;
        }
        candidate = decoded;
      } catch {
        break;
      }
    }
    if (/^https?:\/[^/]/i.test(candidate)) {
      candidate = candidate.replace(/^(https?):\/([^/])/, '$1://$2');
    }

    if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
      return candidate;
    }

    const clientOrigin = this.resolveClientAppOriginFromReferrer();
    if (clientOrigin) {
      const path = candidate.startsWith('/') ? candidate : `/${candidate}`;
      return `${clientOrigin}${path}`;
    }

    return `${window.location.origin}${candidate.startsWith('/') ? candidate : `/${candidate}`}`;
  }

  private resolveRedirectUrlForBackend(): string {
    const candidate = this.redirectUrl?.trim() || '/';

    if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
      return candidate;
    }

    // Path relativo na query (ex.: %2F) é rota do app cliente, não do SSO.
    const clientOrigin = this.resolveClientAppOriginFromReferrer();
    if (clientOrigin) {
      const path = candidate.startsWith('/') ? candidate : `/${candidate}`;
      return `${clientOrigin}${path}`;
    }

    try {
      const parsed = new URL(candidate, window.location.origin);
      if (parsed.origin !== window.location.origin) {
        return parsed.toString();
      }
    } catch {
      if (candidate.startsWith('http')) {
        return candidate;
      }
    }

    return `${window.location.origin}${candidate.startsWith('/') ? candidate : `/${candidate}`}`;
  }

  /** Origem do app que redirecionou ao SSO (Referer), quando redirect_url é só path. */
  private resolveClientAppOriginFromReferrer(): string | null {
    if (typeof document === 'undefined' || !document.referrer) {
      return null;
    }

    try {
      const ref = new URL(document.referrer);
      if (ref.origin !== window.location.origin) {
        return ref.origin;
      }
    } catch {
      return null;
    }

    return null;
  }

  /** Fallback quando o backend ainda não retorna callback_url. */
  private executarRedirecionamentoLegado(accessToken?: string | null): void {
    const externalTarget = this.resolveExternalRedirectTarget();

    if (externalTarget) {
        const callbackUrl = new URL('/login', externalTarget.origin);
        const hashParams = new URLSearchParams();
        hashParams.set(
          'redirect_url',
          `${externalTarget.pathname}${externalTarget.search}${externalTarget.hash}`
        );
        if (accessToken) {
          hashParams.set('access_token', accessToken);
        }
        callbackUrl.hash = hashParams.toString();
        window.location.href = callbackUrl.toString();
        return;
    }

    this.router.navigateByUrl(this.redirectUrl);
  }

  private resolveExternalRedirectTarget(): URL | null {
    const candidates = [this.legacyClientAppUrl, this.redirectUrl];

    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }

      try {
        const parsed = new URL(candidate, window.location.origin);
        if (parsed.origin !== window.location.origin) {
          return parsed;
        }
      } catch {
        if (candidate.startsWith('http') || candidate.includes('localhost')) {
          return new URL(candidate);
        }
      }
    }

    return null;
  }

  onResolved(token: string|null|undefined) {
    this.captchaService.setVerified(true);
  }

  onError() {
    this.captchaService.setVerified(false);
  }
}
